import AddIcon from '@mui/icons-material/Add';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import DoDisturbIcon from '@mui/icons-material/DoDisturb';
import PauseOutlinedIcon from '@mui/icons-material/PauseOutlined';
import PlayArrowOutlinedIcon from '@mui/icons-material/PlayArrowOutlined';
import { Typography } from '@mui/material';
import { Link } from 'react-router-dom';
import type { FlatListTableProps, ListTableAction, ListTableColumn } from 'framework/ui/ui-builder';
import { EMPTY_VALUE_INDICATOR } from 'framework/utils';
import { POSITION_PROFILE_STATUS_LABELS, POSITION_PROFILE_STATUSES } from '../constants';
import type { PositionProfileListRow } from '../types';

type PositionProfileTableActions = {
  canCreate: boolean;
  canUpdate: boolean;
  canDeprecate: boolean;
  canReactivate: boolean;
  canRetire: boolean;
  onCreate: () => void;
  onEdit: (profile: PositionProfileListRow) => void;
  onDeprecate: (profile: PositionProfileListRow) => void;
  onReactivate: (profile: PositionProfileListRow) => void;
  onRetire: (profile: PositionProfileListRow) => void;
};

const getColumns = (getDetailsUrl: (id: string) => string): ListTableColumn<PositionProfileListRow>[] => [
  {
    id: 'name',
    label: 'Profile name',
    render: row => <Link to={getDetailsUrl(row.id)}>{row.name}</Link>,
    sortable: true,
    width: 180,
  },
  {
    id: 'specializationId',
    label: 'Specialization',
    render: row => row.specializationLabel,
    width: 150,
  },
  {
    id: 'technologyId',
    label: 'Technology',
    render: row => row.technologyLabel ?? EMPTY_VALUE_INDICATOR,
    width: 130,
  },
  {
    id: 'competencyLevel',
    label: 'Level',
    render: row => row.competencyLevel,
    sortable: true,
    width: 110,
  },
  {
    id: 'status',
    label: 'Status',
    render: row => POSITION_PROFILE_STATUS_LABELS[row.status] || row.status,
    sortable: true,
    width: 120,
  },
  {
    id: 'nonRetiredLinkedPositionsCount',
    label: 'Used by positions',
    width: 170,
    render: row => (
      <Typography color={row.nonRetiredLinkedPositionsCount ? 'primary' : 'textSecondary'} sx={{ textAlign: 'center' }}>
        {row.nonRetiredLinkedPositionsCount || EMPTY_VALUE_INDICATOR}
      </Typography>
    ),
  },
];

const getActions = ({
  canUpdate,
  canDeprecate,
  canReactivate,
  canRetire,
  onEdit,
  onDeprecate,
  onReactivate,
  onRetire,
}: PositionProfileTableActions): ListTableAction<PositionProfileListRow>[] => [
  {
    id: 'edit-profile',
    label: 'Edit position profile',
    icon: <EditOutlinedIcon />,
    variant: 'outlined' as const,
    tooltip: 'Edit position profile',
    isVisible: (row: PositionProfileListRow) => canUpdate && row.status === POSITION_PROFILE_STATUSES.ACTIVE,
    onClick: onEdit,
  },
  {
    id: 'deprecate-profile',
    label: 'Deprecate position profile',
    icon: <PauseOutlinedIcon />,
    variant: 'outlined' as const,
    tooltip: 'Deprecate position profile',
    isVisible: (row: PositionProfileListRow) => canDeprecate && row.status === POSITION_PROFILE_STATUSES.ACTIVE,
    onClick: onDeprecate,
  },
  {
    id: 'reactivate-profile',
    label: 'Reactivate position profile',
    icon: <PlayArrowOutlinedIcon />,
    variant: 'outlined' as const,
    tooltip: 'Reactivate position profile',
    isVisible: (row: PositionProfileListRow) => canReactivate && row.status === POSITION_PROFILE_STATUSES.DEPRECATED,
    onClick: onReactivate,
  },
  {
    id: 'retire-profile',
    label: 'Retire position profile',
    icon: <DoDisturbIcon />,
    color: 'error',
    variant: 'outlined' as const,
    tooltip: 'Retire position profile',
    isVisible: (row: PositionProfileListRow) =>
      canRetire && row.status === POSITION_PROFILE_STATUSES.DEPRECATED && !row.nonRetiredLinkedPositionsCount,
    onClick: onRetire,
  },
];

export const getPositionProfilesTableConfig = (
  rows: PositionProfileListRow[],
  getDetailsUrl: (id: string) => string,
  actions: PositionProfileTableActions,
): FlatListTableProps<PositionProfileListRow> => ({
  rows,
  columns: getColumns(getDetailsUrl),
  rowActions: {
    actions: getActions(actions),
  },
  mainAction: actions.canCreate
    ? {
        id: 'create-profile',
        label: 'Create profile',
        icon: <AddIcon />,
        variant: 'contained',
        tooltip: 'Create position profile',
        onClick: actions.onCreate,
      }
    : undefined,
  emptyContent: 'No position profiles found.',
});
